import React, { memo } from "react";
import { Canvas } from "@evergis/sgis/es/painters/DomPainter/Canvas";
import { GeometryType } from "@evergis/api";
import { convertSvgToBase64, manipulateSvgSymbol, SGisSymbol, SvgPointSymbol, SymbolByType } from "@evergis/react";

import { SVG } from "@svgdotjs/svg.js";

import { PlaceholderIcon } from "../../components/Placeholder/PlaceholderIcon";
import { SYMBOL_LIMITS } from "../../components/SymbolEditor/limits";
import { getParameterValue } from "../../evergis/core/classification/getFromClassified";
import {
  isSizableSymbol,
  isTwoDimensionalSymbol,
  StyleSymbolType,
  ClassifyAble,
  isSvgPointSymbol,
  ParameterValue,
} from "../../interfaces/style";
import { createCanvasElement } from "../../utils/canvas";
import { deserializeSymbol } from "./SymbolRenderer/deserializeSymbol";
import { getBlankSymbol } from "./SymbolRenderer/getBlankSymbol";
import { SymbolType, getFeatureToRender } from "./SymbolRenderer/getSymbolRenderers";
import { renderSymbolToCanvas } from "./SymbolRenderer/SymbolRenderer";
import { formatSymbol } from "./SymbolRenderer/formatSymbol";
import { svgToPng } from "../../utils/image";

const errorFillColor = {
  strokeColor: "#ff6933",
  fillColor: "#ff6933",
};

const minimizeParameter = (parameter: ClassifyAble<number>, maxValue: number) =>
  Math.min(getParameterValue(parameter) || maxValue, maxValue);

const minimizeSymbol = <S extends StyleSymbolType>(symbol: S, maxSize: number): S => {
  const extraData: Record<string, number> = {};

  if (isTwoDimensionalSymbol(symbol)) {
    extraData.height = minimizeParameter(symbol.height, maxSize);
    extraData.width = minimizeParameter(symbol.width, maxSize);
  }

  if (isSizableSymbol(symbol)) {
    extraData.size = minimizeParameter(symbol.size, maxSize);
  }

  return {
    ...symbol,
    ...extraData,
  };
};

const PureSymbol = memo(({ symbol, size }: { symbol: StyleSymbolType; size: number }) => (
  <SymbolByType
    symbol={deserializeSymbol(minimizeSymbol(symbol, size)) as SGisSymbol}
    size={size}
    type={symbol.type}
    svg={getParameterValue((symbol as SvgPointSymbol).data as ParameterValue) as string}
  />
));

// TODO why don't use componentDidCatch ?
export const getSymbol = (symbol: StyleSymbolType) => {
  if (symbol) {
    try {
      return <PureSymbol symbol={symbol} size={SYMBOL_LIMITS.PREVIEW_SIZE_MAX} />;
    } catch {
      return <PlaceholderIcon />;
    }
  } else {
    return <PlaceholderIcon />;
  }
};

export function getSymbolWithError(source: StyleSymbolType | GeometryType): JSX.Element {
  return getSymbol({ ...(typeof source === "string" ? getBlankSymbol(source) : source), ...errorFillColor });
}

export function getSymbolImage(symbol: StyleSymbolType, size: number, isLegend = false) {
  return getSgisSymbolImage(deserializeSymbol(symbol) as SymbolType, size, isLegend);
}

export async function getSgisSymbolImage(baseSymbol: SymbolType, size: number, isLegend = false) {
  const symbol = await formatSymbol(baseSymbol, { size });

  if (isSvgPointSymbol(baseSymbol as SvgPointSymbol)) {
    const svg = manipulateSvgSymbol({ SVGContainer: SVG(), symbol: baseSymbol as SvgPointSymbol });

    const symbolImage = await svgToPng(
      convertSvgToBase64(svg.svg()),
      getParameterValue((baseSymbol as SvgPointSymbol).size),
    );

    return symbolImage;
  }

  const canvas = createCanvasElement(size, size);
  const sGisCanvas = new Canvas(canvas);

  await renderSymbolToCanvas(sGisCanvas, symbol, size, getFeatureToRender(symbol, isLegend));

  return canvas.toDataURL("image/png");
}
